import React, { useState, useEffect } from "react";
import { Table, Typography, Space, Input, message, Button, Form, Select, Card, Row, Col, Checkbox, Progress, Tag, Alert } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import AxiosOICStat from "AxiosOICStat";
import { useSelector } from 'react-redux';
import { SearchOutlined, ClearOutlined, DownloadOutlined } from '@ant-design/icons';
import DatePicker from 'react-multi-date-picker';
import DatePanel from "react-multi-date-picker/plugins/date_panel"
import PresetValuesPlugin from '../datePickerPlugins/PresetValuesPlugin';
import { getIndicatorName } from '../../utils/indicatorName';

const { Text } = Typography;
const { Option } = Select;

export default function IloConvertFetchTab({ onConvertSuccess, refreshTrigger }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 100,
    total: 0,
    totalPages: 0
  });
  const [form] = Form.useForm();
  const [countryMappings, setCountryMappings] = useState([]);
  const [indicatorMappings, setIndicatorMappings] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [unavailableIndicators, setUnavailableIndicators] = useState([]);
  const countriesState = useSelector((state) => state.countries);
  const indicatorsState = useSelector((state) => state.indicators);
  const [indicatorSearchFilter, setIndicatorSearchFilter] = useState('');
  const [selectedYears, setSelectedYears] = useState([]);

  useEffect(() => {
    fetchCountryMappings();
    fetchIndicatorMappings();
  }, [refreshTrigger]);

  const fetchCountryMappings = async () => {
    try {
      const res = await AxiosOICStat.get("/external_api/ilo/country-mappings");
      setCountryMappings(res.data.mappings || []);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to load country mappings.";
      message.error(detail);
    }
  };

  const fetchIndicatorMappings = async () => {
    try {
      const res = await AxiosOICStat.get("/external_api/ilo/indicator-mappings");
      const mappings = res.data.mappings || [];
      setIndicatorMappings(mappings);
      setUnavailableIndicators(mappings.filter(m => m.status === false));
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to load indicator mappings.";
      message.error(detail);
    }
  };

  const handleSearch = async (values) => {
    if (!values.ilo_codes && !values.ind_codes) {
      message.warning("Please provide either ILO codes or OIC indicator codes");
      return;
    }

    // Calculate start_year and end_year from selectedYears
    let startYear = undefined;
    let endYear = undefined;
    if (selectedYears && selectedYears.length > 0) {
      const yearVals = [];
      selectedYears.forEach(range => {
        if (range.length === 2) {
          for (let i = range[0].year; i <= range[1].year; i++) {
            yearVals.push(i);
          }
        } else {
          yearVals.push(range[0].year);
        }
      });
      if (yearVals.length > 0) {
        startYear = Math.min(...yearVals);
        endYear = Math.max(...yearVals);
      }
    }

    setLoading(true);
    try {
      const requestData = {
        ilo_codes: values.ilo_codes || undefined,
        ind_codes: values.ind_codes || undefined,
        c_code: values.c_code ?? undefined,
        start_year: startYear,
        end_year: endYear,
        page: 1,
        page_size: pagination.pageSize
      };

      const res = await AxiosOICStat.post("/external_api/ilo/convert/bulk", requestData);

      setData(res.data.data || []);
      setPagination({
        current: res.data.page,
        pageSize: res.data.page_size,
        total: res.data.total,
        totalPages: res.data.total_pages
      });
      message.success(`Found ${res.data.total} records`);
      // Check for unavailable indicators
      const selectedCodes = values.ind_codes || [];
      const failedMappings = selectedCodes
        .map(code => indicatorMappings.find(m => m.ind_code === String(code)))
        .filter(m => m && m.status === false);
      setUnavailableIndicators(failedMappings);
      if (onConvertSuccess) onConvertSuccess();
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to convert/fetch ILO data. Please try again.";
      message.error(detail);
      setData([]);
      setPagination({
        current: 1,
        pageSize: 100,
        total: 0,
        totalPages: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = async (newPagination) => {
    const values = form.getFieldsValue();
    if (!values.ilo_codes && !values.ind_codes) {
      return;
    }

    // Calculate start_year and end_year from selectedYears
    let startYear = undefined;
    let endYear = undefined;
    if (selectedYears && selectedYears.length > 0) {
      const yearVals = [];
      selectedYears.forEach(range => {
        if (range.length === 2) {
          for (let i = range[0].year; i <= range[1].year; i++) {
            yearVals.push(i);
          }
        } else {
          yearVals.push(range[0].year);
        }
      });
      if (yearVals.length > 0) {
        startYear = Math.min(...yearVals);
        endYear = Math.max(...yearVals);
      }
    }

    setLoading(true);
    try {
      const requestData = {
        ilo_codes: values.ilo_codes || undefined,
        ind_codes: values.ind_codes || undefined,
        c_code: values.c_code ?? undefined,
        start_year: startYear,
        end_year: endYear,
        page: newPagination.current,
        page_size: newPagination.pageSize
      };

      const res = await AxiosOICStat.post("/external_api/ilo/convert/bulk", requestData);

      setData(res.data.data || []);
      setPagination({
        current: res.data.page,
        pageSize: res.data.page_size,
        total: res.data.total,
        totalPages: res.data.total_pages
      });
      if (onConvertSuccess) onConvertSuccess();
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to fetch page. Please try again.";
      message.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    form.resetFields();
    setData([]);
    setPagination({
      current: 1,
      pageSize: 100,
      total: 0,
      totalPages: 0
    });
    setSelectedYears([]);
  };

  const handleExportCsv = async () => {
    setExportLoading(true);
    setExportProgress(0);

    try {
      const startRes = await AxiosOICStat.post("/external_api/ilo/export-xlsx/start");
      const taskId = startRes.data.task_id;

      const pollStatus = async () => {
        try {
          const statusRes = await AxiosOICStat.get(`/external_api/ilo/export-xlsx/status/${taskId}`);
          const { status, progress } = statusRes.data;

          setExportProgress(progress);

          if (status === "completed") {
            const downloadRes = await AxiosOICStat.get(`/external_api/ilo/export-xlsx/download/${taskId}`, {
              responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([downloadRes.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `ilo_data_export_${taskId}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            message.success("Export successful");
            setExportLoading(false);
            setTimeout(() => setExportProgress(0), 2000);
          } else if (status === "failed") {
            message.error("Export failed on server");
            setExportLoading(false);
            setTimeout(() => setExportProgress(0), 2000);
          } else {
            setTimeout(pollStatus, 1000);
          }
        } catch (err) {
          message.error("Error polling export status");
          setExportLoading(false);
          setTimeout(() => setExportProgress(0), 2000);
        }
      };

      pollStatus();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Failed to start CSV export.";
      message.error(detail);
      setExportLoading(false);
    }
  };

  const columns = [
    {
      title: "Country Name",
      dataIndex: "c_code",
      key: "country_name",
      width: 180,
      sorter: (a, b) => {
        const aName = countriesState.value?.[a.c_code]?.c_short_name_eng || '';
        const bName = countriesState.value?.[b.c_code]?.c_short_name_eng || '';
        return aName.localeCompare(bName);
      },
      render: (cCode) => {
        const country = countriesState.value?.[cCode];
        return country ? (
          <Text>{country.c_short_name_eng}</Text>
        ) : (
          <Text type="secondary">—</Text>
        );
      },
    },
    {
      title: "Indicator Code",
      dataIndex: "ind_code",
      key: "ind_code",
      width: 120,
      sorter: (a, b) => (a.ind_code || '').localeCompare(b.ind_code || ''),
      render: (val) => <Text strong>{val}</Text>,
    },
    {
      title: "Indicator Name",
      dataIndex: "ind_code",
      key: "indicator_name",
      width: 200,
      sorter: (a, b) => {
        const aName = getIndicatorName(indicatorsState.value?.[a.ind_code]) || '';
        const bName = getIndicatorName(indicatorsState.value?.[b.ind_code]) || '';
        return aName.localeCompare(bName);
      },
      render: (indCode) => {
        const indicator = indicatorsState.value?.[indCode];
        return indicator ? (
          <Text>{getIndicatorName(indicator)}</Text>
        ) : (
          <Text type="secondary">—</Text>
        );
      },
    },
    {
      title: "ILO Code",
      dataIndex: "ilo_code",
      key: "ilo_code",
      width: 180,
      sorter: (a, b) => (a.ilo_code || '').localeCompare(b.ilo_code || ''),
      render: (val) => val ? <Text code>{val}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: "Year",
      dataIndex: "year",
      key: "year",
      width: 100,
      sorter: (a, b) => (a.year || 0) - (b.year || 0),
      render: (val) => val ? <Text>{val}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: "ILO Value",
      dataIndex: "ilo_value",
      key: "ilo_value",
      width: 120,
      sorter: (a, b) => (a.ilo_value || 0) - (b.ilo_value || 0),
      render: (val) => val !== null && val !== undefined ? <Text>{val}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: "OIC Value",
      dataIndex: "oic_value",
      key: "oic_value",
      width: 120,
      sorter: (a, b) => (a.oic_value || 0) - (b.oic_value || 0),
      render: (val) => val !== null && val !== undefined ? <Text>{val}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: "Match",
      dataIndex: "match",
      key: "match",
      width: 120,
      sorter: (a, b) => (a.match === b.match) ? 0 : a.match ? -1 : 1,
      render: (val) => (
        <Tag color={val ? 'green' : 'red'}>
          {val ? 'Match' : 'No Match'}
        </Tag>
      ),
    },
  ];

  const availableCountries = countryMappings
    .filter(mapping => countriesState.value?.[mapping.c_code])
    .map(mapping => ({
      ...countriesState.value[mapping.c_code],
      c_code: mapping.c_code
    }))
    .sort((a, b) => {
      const aName = a.c_name_eng || '';
      const bName = b.c_name_eng || '';
      return aName.localeCompare(bName);
    });

  const availableIloCodes = [...new Set(
    indicatorMappings
      .map(mapping => mapping.ilo_code)
      .filter(code => code)
  )].sort((a, b) => a.localeCompare(b));

  const uniqueIndicators = new Map();
  indicatorMappings
    .filter(mapping => indicatorsState.value?.[mapping.ind_code])
    .forEach(mapping => {
      if (!uniqueIndicators.has(mapping.ind_code)) {
        uniqueIndicators.set(mapping.ind_code, {
          ...indicatorsState.value[mapping.ind_code],
          ind_code: mapping.ind_code
        });
      }
    });

  const availableIndicators = Array.from(uniqueIndicators.values())
    .sort((a, b) => {
      const aName = getIndicatorName(a) || '';
      const bName = getIndicatorName(b) || '';
      return aName.localeCompare(bName);
    });

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      {unavailableIndicators.length > 0 && (
        <Alert
          type="error"
          showIcon
          closable
          message={`${unavailableIndicators.length} indicator(s) unavailable in ILO API`}
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {unavailableIndicators.map(m => {
                const indInfo = indicatorsState.value?.[m.ind_code];
                const displayName = getIndicatorName(indInfo) || m.ind_code;
                return (
                  <li key={m.id}>
                    <Text code>{m.ilo_code}</Text>
                    {m.ind_code ? ` (OIC: ${m.ind_code})` : ''}
                    {displayName ? ` \u2014 ${displayName}` : ''}
                  </li>
                );
              })}
            </ul>
          }
          onClose={() => setUnavailableIndicators([])}
        />
      )}
      <Card>
        <Form form={form} layout="vertical" onFinish={handleSearch}>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                name="ind_codes"
                label="OIC Indicators"
              >
                <Select
                  mode="multiple"
                  showSearch
                  allowClear
                  placeholder="Select indicators"
                  searchValue={indicatorSearchFilter}
                  onSearch={(val) => setIndicatorSearchFilter(val)}
                  autoClearSearchValue={false}
                  dropdownRender={(menu) => {
                    const filteredOptions = availableIndicators.filter(i => {
                      const searchText = `${i.ind_code} ${getIndicatorName(i)}`.toLowerCase();
                      return searchText.indexOf(indicatorSearchFilter.toLowerCase()) >= 0;
                    });
                    const selectedVals = form.getFieldValue("ind_codes") || [];
                    const allFilteredSelected = filteredOptions.length > 0 && filteredOptions.every(opt => selectedVals.includes(opt.ind_code));
                    const someFilteredSelected = filteredOptions.some(opt => selectedVals.includes(opt.ind_code));

                    return (
                      <>
                        <div style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                          <Checkbox
                            checked={allFilteredSelected}
                            indeterminate={someFilteredSelected && !allFilteredSelected}
                            onChange={(e) => {
                              const currentSelected = form.getFieldValue("ind_codes") || [];
                              const filteredCodes = filteredOptions.map(opt => opt.ind_code);
                              if (e.target.checked) {
                                form.setFieldsValue({
                                  ind_codes: [...new Set([...currentSelected, ...filteredCodes])]
                                });
                              } else {
                                form.setFieldsValue({
                                  ind_codes: currentSelected.filter(code => !filteredCodes.includes(code))
                                });
                              }
                            }}
                          >
                            Select All in Results
                          </Checkbox>
                        </div>
                        {menu}
                      </>
                    );
                  }}
                  filterOption={(input, option) => {
                    const indicator = availableIndicators.find(i => i.ind_code === option.value);
                    if (!indicator) return false;
                    const searchText = `${indicator.ind_code} ${getIndicatorName(indicator)}`.toLowerCase();
                    return searchText.indexOf(input.toLowerCase()) >= 0;
                  }}
                >
                  {availableIndicators.map(indicator => (
                    <Option key={indicator.ind_code} value={indicator.ind_code}>
                      {indicator.ind_code} - {getIndicatorName(indicator)}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="c_code"
                label="Country Filter (Optional)"
              >
                <Select
                  showSearch
                  allowClear
                  placeholder="Filter by country"
                  filterOption={(input, option) => {
                    const country = availableCountries.find(c => c.c_code === option.value);
                    if (!country) return false;
                    const searchText = `${country.c_code} ${country.c_short_name_eng}`.toLowerCase();
                    return searchText.indexOf(input.toLowerCase()) >= 0;
                  }}
                >
                  {availableCountries.map(country => (
                    <Option key={country.c_code} value={country.c_code}>
                      {country.c_code} - {country.c_short_name_eng}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Years (Optional)">
                <DatePicker
                  onlyYearPicker
                  multiple
                  range
                  sort
                  highlightToday={false}
                  placeholder="Select years"
                  value={selectedYears}
                  onChange={setSelectedYears}
                  maxDate={new Date()}
                  minDate={new Date(1970, 0, 1)}
                  plugins={[
                    <DatePanel />,
                    <PresetValuesPlugin position="left" setValue={setSelectedYears} />,
                  ]}
                  style={{ width: '100%', height: '32px', marginTop: '-1px' }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SearchOutlined />}
              loading={loading}
            >
              Convert / Fetch
            </Button>
            <Button
              icon={<ClearOutlined />}
              onClick={handleClear}
            >
              Clear
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExportCsv}
              loading={exportLoading}
            >
              Export All Data to CSV
            </Button>
          </Space>
          {exportLoading || exportProgress > 0 ? (
            <div style={{ marginTop: '16px' }}>
              <Progress
                percent={exportProgress}
                status={exportProgress === 100 ? 'success' : 'active'}
                strokeColor={{
                  '0%': '#108ee9',
                  '100%': '#87d068',
                }}
              />
              <div style={{ textAlign: 'center', marginTop: '4px', color: '#666', fontSize: '12px' }}>
                Preparing CSV export, please wait...
              </div>
            </div>
          ) : null}
        </Form>
      </Card>

      <Table
        rowKey={(record, index) => `${record.c_code}-${record.ind_code}-${record.year}-${index}`}
        loading={loading}
        columns={columns}
        dataSource={data}
        size="middle"
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
          pageSizeOptions: ['50', '100', '200', '500']
        }}
        onChange={handleTableChange}
        bordered
        scroll={{ x: 1600 }}
      />
    </Space>
  );
}
